import { casesI18n } from '@app-builder/components';
import { CasePivotValues } from '@app-builder/components/Cases/CasePivotValues';
import { IngestedObjectDetailModal } from '@app-builder/components/Data/IngestedObjectDetailModal';
import { CaseDetailTriggerObject } from '@app-builder/components/Decisions/TriggerObjectDetail';
import { ScreeningQueryDetail } from '@app-builder/components/Screenings/ScreeningQueryDetail';
import { ScreeningReviewSection } from '@app-builder/components/Screenings/SreeningReview';
import { useAgnosticNavigation } from '@app-builder/contexts/AgnosticNavigationContext';
import { usePivotValues } from '@app-builder/hooks/decisions/usePivotValues';
import { fromUUIDtoSUUID } from '@app-builder/utils/short-uuid';
import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_app/_builder/cases/$caseId/d/$decisionId/screenings/$screeningId/hits')({
  component: CaseSanctionsHitsPage,
});

function CaseSanctionsHitsPage() {
  const { t } = useTranslation(casesI18n);
  const { caseDetail, screening, decision, dataModel, pivots } = useLoaderData({
    from: '/_app/_builder/cases/$caseId/d/$decisionId/screenings/$screeningId',
  });
  const pivotValues = usePivotValues(decision.pivotValues, pivots);
  const [objectLink, setObjectLink] = useState<{
    tableName: string;
    objectId: string;
  } | null>(null);
  const navigate = useAgnosticNavigation();

  return (
    <div className="bg-surface-card border-grey-border grid grid-cols-[max-content_2fr_1fr_repeat(3,max-content)] gap-x-6 gap-y-2 rounded-md border">
      <div className="col-span-full flex flex-row gap-3xl p-md">
        <ScreeningReviewSection
          screening={screening}
          onRefineSuccess={(screeningId) => {
            navigate(
              `/cases/${fromUUIDtoSUUID(caseDetail.id)}/d/${fromUUIDtoSUUID(decision.id)}/screenings/${fromUUIDtoSUUID(screeningId)}`,
            );
          }}
        />
        <div className="sticky top-0 flex h-fit flex-1 flex-col gap-lg">
          {screening.request ? (
            <ScreeningQueryDetail request={screening.request} initialQuery={screening.initialQuery} />
          ) : null}
          {pivotValues.length > 0 && (
            <div className="flex h-fit flex-col gap-sm">
              <div className="col-start-2 row-start-1 flex flex-row items-center justify-between gap-sm">
                <span className="text-grey-primary text-xs font-medium first-letter:capitalize">
                  {t('cases:case_detail.pivot_values')}
                </span>
              </div>
              <CasePivotValues pivotValues={pivotValues} />
            </div>
          )}

          <div className="flex h-fit flex-col gap-sm">
            <div className="flex flex-row items-center justify-between gap-sm">
              <span className="text-grey-primary text-xs font-medium first-letter:capitalize">
                {t('cases:case_detail.trigger_object')}
              </span>
            </div>
            <CaseDetailTriggerObject
              className="h-fit max-h-[50dvh] overflow-auto"
              dataModel={dataModel}
              triggerObject={decision.triggerObject}
              triggerObjectType={decision.triggerObjectType}
              onLinkClicked={(tableName, objectId) => setObjectLink({ tableName, objectId })}
            />
            {objectLink ? (
              <IngestedObjectDetailModal
                dataModel={dataModel}
                tableName={objectLink.tableName}
                objectId={objectLink.objectId}
                onClose={() => setObjectLink(null)}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
